import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  MessageCircle,
  User,
  Bot,
  Search,
  Calendar,
  TrendingUp,
  BarChart3,
  Sparkles,
  Filter
} from "lucide-react";
import { getChatStatistics } from "@/lib/faq-service";

interface ChatMessage {
  id: string;
  conversation_id: string;
  user_id: string;
  role: 'user' | 'assistant';
  content: string;
  credits_used: number;
  faq_matched: boolean;
  faq_id: string | null;
  created_at: string;
  profiles?: {
    email: string;
    display_name: string | null;
  };
}

interface ChatStats {
  totalConversations: number;
  totalMessages: number;
  totalFAQMatches: number;
  creditsUsed: number;
}

const ChatLogs = () => {
  const { toast } = useToast();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<ChatMessage[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<"all" | "faq" | "ai">("all");
  const [stats, setStats] = useState<ChatStats>({
    totalConversations: 0,
    totalMessages: 0,
    totalFAQMatches: 0,
    creditsUsed: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChatLogs();
    loadStatistics();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [messages, searchQuery, filterType]);

  const loadChatLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('chat_messages')
        .select(`
          *,
          profiles:user_id (
            email,
            display_name
          )
        `)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) throw error;
      setMessages(data || []);
    } catch (error: any) {
      console.error('Error loading chat logs:', error);
      toast({
        title: "Error Loading Logs",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    const statistics = await getChatStatistics();
    setStats(statistics);
  };

  const applyFilters = () => {
    let filtered = messages;

    // Filter by type
    if (filterType === "faq") {
      filtered = filtered.filter(m => m.faq_matched);
    } else if (filterType === "ai") {
      filtered = filtered.filter(m => !m.faq_matched && m.credits_used > 0);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m =>
        m.content.toLowerCase().includes(query) ||
        m.profiles?.email.toLowerCase().includes(query) ||
        m.profiles?.display_name?.toLowerCase().includes(query)
      );
    }

    setFilteredMessages(filtered);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const faqMatchRate = stats.totalMessages > 0
    ? ((stats.totalFAQMatches / stats.totalMessages) * 100).toFixed(1)
    : "0";

  const creditsSaved = stats.totalFAQMatches * 5;

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalMessages.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {stats.totalConversations} conversations
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">FAQ Match Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{faqMatchRate}%</div>
            <p className="text-xs text-muted-foreground">
              {stats.totalFAQMatches} FAQ matches
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Credits Used</CardTitle>
            <BarChart3 className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.creditsUsed.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              From AI responses
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Credits Saved</CardTitle>
            <Sparkles className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{creditsSaved.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Via FAQ system
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Chat Logs
          </CardTitle>
          <CardDescription>
            View and analyze all user chat interactions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search messages, users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <Tabs value={filterType} onValueChange={(v) => setFilterType(v as any)} className="w-full sm:w-auto">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="all">
                  <Filter className="h-4 w-4 mr-2" />
                  All
                </TabsTrigger>
                <TabsTrigger value="faq">
                  <Sparkles className="h-4 w-4 mr-2" />
                  FAQ
                </TabsTrigger>
                <TabsTrigger value="ai">
                  <Bot className="h-4 w-4 mr-2" />
                  AI
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <Button onClick={loadChatLogs} variant="outline">
              Refresh
            </Button>
          </div>

          {/* Messages List */}
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No messages found
            </div>
          ) : (
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {filteredMessages.map((msg) => (
                <Card key={msg.id} className={`${msg.faq_matched ? 'border-green-500/30 bg-green-500/5' : ''
                  }`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === "user" ? "bg-primary/10 text-primary" : "bg-muted"
                        }`}>
                        {msg.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant={msg.role === "user" ? "default" : "secondary"}>
                              {msg.role}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {msg.profiles?.display_name || msg.profiles?.email || 'Unknown'}
                            </span>
                            {msg.faq_matched && (
                              <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
                                <Sparkles className="h-3 w-3 mr-1" />
                                FAQ Match
                              </Badge>
                            )}
                            {msg.credits_used > 0 && (
                              <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/30">
                                {msg.credits_used} credits
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {formatDate(msg.created_at)}
                          </div>
                        </div>

                        <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="mt-4 text-sm text-muted-foreground text-center">
            Showing {filteredMessages.length} of {messages.length} messages
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatLogs;
